import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutPropertiesLoose from '@babel/runtime/helpers/objectWithoutPropertiesLoose';
import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Icon from './Icon.js';
import { a as addClassNames, c as classNameProps } from './classNames-6a4763fd.js';
import { p as props, d as defaults } from './componentEl-33ff2aeb.js';
import { a as addSpacingClasses, m as marginProps } from './spacing-d13ff368.js';

function AlertAction(_ref) {
  let {
    children,
    componentEl: Component,
    className
  } = _ref,
      otherProps = _objectWithoutPropertiesLoose(_ref, ["children", "componentEl", "className"]);

  let cssClasses = ['st_alert__action'];
  cssClasses = addClassNames(className, cssClasses);
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps), children);
}

AlertAction.propTypes = {
  /** extra CSS classes, if any */
  className: classNameProps,

  /** HTML element or React Component to render as */
  componentEl: props
};
AlertAction.defaultProps = {
  className: null,
  componentEl: 'a'
};
AlertAction.displayName = 'Alert.Action';

function Alert(_ref) {
  let {
    componentEl: Component,
    className,
    icon,
    margin,
    onDismiss,
    type,
    fullWidth,
    floating,
    children,
    autoDismiss,
    autoDismissTime
  } = _ref,
      otherProps = _objectWithoutPropertiesLoose(_ref, ["componentEl", "className", "icon", "margin", "onDismiss", "type", "fullWidth", "floating", "children", "autoDismiss", "autoDismissTime"]);

  let cssClasses = ['st_alert', "st_alert--" + type];
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    margin,
    cssClasses
  });
  if (fullWidth) cssClasses.push('st_alert--fullwidth');
  if (floating) cssClasses.push('st_alert--floating');
  const dismissTimer = useRef(false); // set timer for autodismiss

  useEffect(() => {
    if (autoDismiss) {
      dismissTimer.current = setTimeout(() => {
        if (onDismiss) onDismiss();
      }, autoDismissTime * 1000);
      return () => dismissTimer.current && clearTimeout(dismissTimer.current);
    }

    return undefined;
  }, [autoDismiss, onDismiss, autoDismissTime]); // handle when X is clicked to manually close Alert

  function handleDismissClick() {
    clearTimeout(dismissTimer.current);
    if (onDismiss) onDismiss();
  }

  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' '),
    role: "alert"
  }, otherProps), /*#__PURE__*/React.createElement("div", {
    className: "st_alert__content"
  }, icon ? /*#__PURE__*/React.createElement(Icon, {
    type: icon,
    size: "md",
    className: "st_alert__icon"
  }) : null, children), onDismiss && /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: handleDismissClick,
    size: "md",
    className: "st_alert__dismiss",
    "aria-label": "Close"
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "cross",
    alt: "dismiss"
  })));
}

Alert.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,

  /** extra CSS classes, if any */
  className: classNameProps,

  /** margin for avatar, if any */
  margin: marginProps,

  /** type of alert */
  type: PropTypes.oneOf(['primary', 'success', 'warning', 'error', 'gray']),

  /** whether component should go edge to edge */
  fullWidth: PropTypes.bool,

  /** whether component should be overlaid in the middle of the viewport */
  floating: PropTypes.bool,

  /** optional icon to be displayed to the left */
  icon: PropTypes.string,

  /** callback when alert is dismissed, setting this lets X appear */
  onDismiss: PropTypes.func,

  /** whether to fire onDismiss automatically after X seconds */
  autoDismiss: PropTypes.bool,

  /** how many seconds to wait before firing onDismiss automatically */
  autoDismissTime: PropTypes.number
};
Alert.defaultProps = {
  componentEl: defaults,
  type: 'primary',
  fullWidth: false,
  floating: false,
  margin: null,
  className: null,
  icon: null,
  onDismiss: null,
  autoDismiss: false,
  autoDismissTime: 3
};
Alert.Action = AlertAction;

export default Alert;
